import i18n from '@/i18n';
import { getApiBaseUrl, getApimSubscriptionKey } from '@/config/env';

export interface ApiClientOptions extends RequestInit {
  failMessage?: string;
  baseUrl?: string;
  accessToken?: string;
}

/**
 * Central HTTP client for all API calls. Handles URL resolution, auth headers,
 * JSON serialization, and error handling.
 *
 * @param endpoint Path to the endpoint (e.g. '/user/ballot')
 * @param options Fetch options plus baseUrl, accessToken, and failMessage.
 *   - baseUrl: defaults to backend URL (or APIM when configured). Pass '' for
 *     relative URLs (Express server).
 *   - accessToken: when provided, adds Authorization: Bearer header.
 */
export async function apiClient(endpoint: string, options?: ApiClientOptions): Promise<Response> {
  const { failMessage, baseUrl, accessToken, body, headers, method, ...fetchOptions } =
    options ?? {};

  const resolvedBase = baseUrl !== undefined ? baseUrl : getApiBaseUrl();
  const isBackendCall = baseUrl === undefined;
  const resolvedMethod = method ?? 'GET';
  const partnerKey = isBackendCall ? getApimSubscriptionKey() : undefined;

  const response = await fetch(`${resolvedBase}${endpoint}`, {
    ...fetchOptions,
    method: resolvedMethod,
    headers: {
      ...(headers as Record<string, string>),
      ...(['POST', 'PUT', 'PATCH'].includes(resolvedMethod)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(partnerKey ? { 'Ocp-Apim-Subscription-Key': partnerKey } : {}),
    },
    ...(body ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    throw new Error(failMessage ?? i18n.t('requestFailed', { ns: 'common' }));
  }

  return response;
}
